// -----------------------------------------------------------------------
// <copyright file="BackButton.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { ITheme, ActionButton, Stack } from '@fluentui/react';
import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IAppState, UITabView } from '../../../web-legacy/models/reduxState';
import { popUITabViewStack } from '../../../web-legacy/packages/app/appActions';
import { getCorrelationId } from '../../../web-legacy/packages/app/selectors/contactUsSelector';
import { navigateBack } from '../../../web-legacy/packages/contactUs/reduxUpdater/actions/modalitySectionActions';
import { getHelpLogger } from '../../utils/getHelpLogger';
import { capturePageAction } from '../../utils/oneDsUtil';

import { isGetHelpSourceApp } from '../../utils/statelessUtil';
import { getString } from '../../utils/stringLoader';
import { getBackBtnIconStyles, backBtnLabelStyles, backButtonStyles } from './BackButton.styles';

interface IProps {
  theme?: ITheme;
  onRenderBackButtonComplete?: (isVisible: boolean) => void;
}

export const BackButton = (props: IProps): JSX.Element => {
  const uITabViewStack = useSelector((appState: IAppState) => appState.NavState.uITabViewStack);
  const currentUITabView =
    uITabViewStack && uITabViewStack.length > 0 ? uITabViewStack[uITabViewStack.length - 1] : null;

  const backbtnString = getString('requestCreation_backButtonText');
  const correlationId = useSelector(getCorrelationId);

  const serviceRequestPages = useSelector(
    (appState: IAppState) => appState.ContactState?.ModalitySectionState?.serviceRequestPages
  );
  const currentServiceRequestPage =
    serviceRequestPages && serviceRequestPages.length > 0 ? serviceRequestPages[serviceRequestPages.length - 1] : null;
  const buttonExistsForPage = currentServiceRequestPage ? currentServiceRequestPage.isBackButtonPresent : false;
  const dispatch = useDispatch();
  const dispatchNavigateBack = useCallback(() => dispatch(navigateBack()), [dispatch]);
  const dispatchPopUITabViewStack = useCallback(() => dispatch(popUITabViewStack()), [dispatch]);
  const isGetHelp = isGetHelpSourceApp(useSelector((appState: IAppState) => appState.GlobalState.sourceApp));
  if (
    (currentUITabView?.uITabView === UITabView.requestCreation && !buttonExistsForPage) ||
    currentUITabView?.uITabView === UITabView.searchHelp
  ) {
    props.onRenderBackButtonComplete?.(false);
    return null;
  }

  props.onRenderBackButtonComplete?.(true);

  const onBackBtnClick = () => {
    const logHandlerTags = {
      content: {
        contentId: 'uITabViewBackBtnClick',
        contentName: 'uITabViewBackBtn',
        correlationId: correlationId,
        formnm: 'maxFunnel',
        fileName: 'BackButton.tsx',
        methodName: 'onBackBtnClick',
        areaName: 'modalitiesSection',
      },
      behavior: 'CLICK',
    };
    capturePageAction(null, logHandlerTags);

    // if in process of request creation go back to previous page, change the tab in all other cases
    if (currentUITabView?.uITabView === UITabView.requestCreation && currentServiceRequestPage) {
      dispatchNavigateBack();

      // If Service Request pages stack is empty or was on last page, go back to previous tab
      if (serviceRequestPages.length <= 1) {
        dispatchPopUITabViewStack();
      }
    } else {
      dispatchPopUITabViewStack();
    }

    if (currentUITabView?.uITabView === UITabView.articleReader) {
      getHelpLogger.logBackButtonFromArticleReaderClicked();
    }
  };

  return (
    <ActionButton
      iconProps={{ iconName: 'ChromeBack', styles: getBackBtnIconStyles(props) }}
      styles={backButtonStyles(isGetHelp, props.theme.palette.neutralPrimary)}
      onClick={onBackBtnClick}
      aria-label={backbtnString}
    >
      <Stack styles={backBtnLabelStyles}>{backbtnString}</Stack>
    </ActionButton>
  );
};
